import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateOatfiPaymentMethod,
  AdminFetchOatfiBusiness,
  AdminFetchPlaidLinkToken,
  AdminRequestOatfiUnderwriting,
  AdminSaveOatfiBusiness,
  AdminUploadHistoricalInvoices,
  AdminUploadOatfiSupplementalData,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { SupplementalDataType } from 'src/constants/oatfi-business/enums'
import type {
  OatfiBusiness,
  OatfiBusinessFormFields,
  PlaidLink,
  SaveOatfiBusinessPayload,
  UploadHistoricalInvoicesPayload,
  UploadSupplementalDataPayload,
} from 'src/interfaces/oatfiBusiness'
import { parseOatfiBusinessResponse, parsePlaidLinkResponse } from 'src/utils/oatfiBusiness'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface PartnerScopedPayload {
  partnerId?: string
}

const partnerIdParams = (partnerId: string) => {
  return [{ field: ':partnerId', value: partnerId }]
}

/** The seven optional fields, as `[form key, wire key]`. */
const OPTIONAL_STRING_FIELDS: Array<keyof OatfiBusinessFormFields> = [
  'dbaName',
  'contactEmail',
  'businessStartDateMonth',
  'businessStartDateYear',
  'businessEntityType',
  'requestedLimitSource',
]

/**
 * Converts the form's all-strings shape into the request body.
 *
 * Two rules, both load-bearing:
 *
 * 1. **Empty optionals are omitted, never sent as `''`.** `businessEntityType` and
 *    `requestedLimitSource` are `@IsEnum`-validated, so `''` is a 400 rather than "unset".
 * 2. **`requestedCreditLimit` is parsed to a number of cents.** The backend carries only
 *    `@IsOptional()` on it — no `@IsInt`, no `@Min` — and forwards it to Oatfi untouched, so a
 *    string would reach the lender verbatim. `'0'` is a meaningful value and must survive, so
 *    the check is on `=== ''`, not on falsiness.
 *
 * The nine required fields are always sent. `PUT` uses the same non-partial DTO as `POST`, so
 * there is no sparse-update variant of this function.
 */
export function toOatfiBusinessBody(fields: OatfiBusinessFormFields): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: fields.name,
    city: fields.city,
    streetAddress: fields.streetAddress,
    state: fields.state,
    postalCode: fields.postalCode,
    taxId: fields.taxId,
    contactFirstName: fields.contactFirstName,
    contactLastName: fields.contactLastName,
    contactPhoneNumber: fields.contactPhoneNumber,
  }

  OPTIONAL_STRING_FIELDS.forEach((field) => {
    const value = fields[field]

    if (value !== '') {
      body[field] = value
    }
  })

  if (fields.requestedCreditLimit !== '') {
    body.requestedCreditLimit = Number(fields.requestedCreditLimit)
  }

  return body
}

function assertPlaidLink(payload: unknown): PlaidLink {
  const link = parsePlaidLinkResponse(payload)

  if (link === null) {
    throw new Error('Plaid link-token response is not a link token')
  }

  return link
}

export function* onFetchOatfiBusiness(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchOatfiBusiness.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    let business: OatfiBusiness | null = null

    try {
      const { payload }: { payload: unknown } = yield call(
        callSecureApi,
        Endpoints.AdminFetchOatfiBusiness,
        {
          method: 'GET',
          params: partnerIdParams(partnerId),
        },
      )

      business = parseOatfiBusinessResponse(payload)
    } catch (error: unknown) {
      // A customer with no Oatfi business is the common case and answers 404 — an expected
      // result, not a failure, so it resolves as `business: null` and is deliberately never
      // sent to Sentry. The backend uses the same 404 for 'Partner not found', which this tab
      // cannot distinguish. That is safe here: a nonexistent customerId is not reachable
      // through normal navigation, and if it were, the create form's POST would surface the
      // backend's own 'Partner not found' message.
      if ((error as { status?: number } | null)?.status !== 404) {
        throw error
      }
    }

    yield put(AdminFetchOatfiBusiness.success({ partnerId, business }))

    if (action.next) {
      action.next(null, business)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oatfiBusiness.onFetchOatfiBusiness] error' })

    yield put(AdminFetchOatfiBusiness.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the Oatfi business'))
    }
  }
}

export function* onSaveOatfiBusiness(action: AsyncAction<SaveOatfiBusinessPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminSaveOatfiBusiness.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  const isCreate = action.payload.mode === 'create'

  try {
    // The body is built explicitly rather than forwarded so `partnerId` — which belongs in the
    // path — never reaches the body, where the DTO would reject it.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      isCreate ? Endpoints.AdminCreateOatfiBusiness : Endpoints.AdminUpdateOatfiBusiness,
      {
        method: isCreate ? 'POST' : 'PUT',
        params: partnerIdParams(partnerId),
        body: toOatfiBusinessBody(action.payload.fields),
      },
    )

    const business = parseOatfiBusinessResponse(payload)

    if (business === null) {
      throw new Error('Oatfi business save response is not a business')
    }

    yield put(AdminSaveOatfiBusiness.success({ partnerId, business }))

    if (action.next) {
      action.next(null, business)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oatfiBusiness.onSaveOatfiBusiness] error' })

    yield put(AdminSaveOatfiBusiness.failed(error))

    if (action.next) {
      action.next(
        resolveErrorMessage(
          error,
          isCreate ? 'Failed to create the Oatfi business' : 'Failed to save the Oatfi business',
        ),
      )
    }
  }
}

export function* onRequestOatfiUnderwriting(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminRequestOatfiUnderwriting.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminRequestOatfiUnderwriting,
      {
        method: 'POST',
        params: partnerIdParams(partnerId),
      },
    )

    let business = parseOatfiBusinessResponse(payload)

    // The POST response is serialized from the repository-update return rather than a fresh
    // read, so a shape that cannot parse must fall back to a GET rather than dispatch a
    // half-populated business over the one already on screen. The OAuth tab hit the same class
    // of bug, where its PUT response could never parse as a client.
    if (business === null) {
      const { payload: fetched }: { payload: unknown } = yield call(
        callSecureApi,
        Endpoints.AdminFetchOatfiBusiness,
        {
          method: 'GET',
          params: partnerIdParams(partnerId),
        },
      )

      business = parseOatfiBusinessResponse(fetched)
    }

    if (business === null) {
      throw new Error('Oatfi underwriting response is not a business')
    }

    yield put(AdminRequestOatfiUnderwriting.success({ partnerId, business }))

    if (action.next) {
      action.next(null, business)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.oatfiBusiness.onRequestOatfiUnderwriting] error',
    })

    yield put(AdminRequestOatfiUnderwriting.failed(error))

    if (action.next) {
      // Any Oatfi-side error arrives as a flat 400 reading 'Failed to request underwriting in
      // Oatfi', with the real cause only in para-node's logs. resolveErrorMessage surfaces
      // that sentence unchanged; the card tells the admin where the detail lives.
      action.next(resolveErrorMessage(error, 'Failed to request underwriting'))
    }
  }
}

export function* onUploadOatfiSupplementalData(action: AsyncAction<UploadSupplementalDataPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminUploadOatfiSupplementalData.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const formData = new FormData()

    formData.append('type', action.payload.type)
    formData.append('file', action.payload.file)

    // `invoiceId` is @ValidateIf'd to the INVOICE type only, so it is appended only there.
    if (action.payload.type === SupplementalDataType.Invoice && action.payload.invoiceId) {
      formData.append('invoiceId', action.payload.invoiceId)
    }

    yield call(callSecureApi, Endpoints.AdminUploadOatfiSupplementalData, {
      method: 'POST',
      params: partnerIdParams(partnerId),
      body: formData,
      resetContentType: true,
    })

    // No payload: there is no read endpoint for supplemental data anywhere in para-node, so a
    // successful upload changes nothing this tab can display.
    yield put(AdminUploadOatfiSupplementalData.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.oatfiBusiness.onUploadOatfiSupplementalData] error',
    })

    yield put(AdminUploadOatfiSupplementalData.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to upload the document'))
    }
  }
}

export function* onUploadHistoricalInvoices(action: AsyncAction<UploadHistoricalInvoicesPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminUploadHistoricalInvoices.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const formData = new FormData()

    formData.append('file', action.payload.file)

    yield call(callSecureApi, Endpoints.AdminUploadHistoricalInvoices, {
      method: 'POST',
      params: partnerIdParams(partnerId),
      body: formData,
      resetContentType: true,
    })

    // Historical invoices are filtered out of the Invoice tab's list server-side, so there is
    // nothing on this tab or that one to refresh.
    yield put(AdminUploadHistoricalInvoices.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.oatfiBusiness.onUploadHistoricalInvoices] error',
    })

    yield put(AdminUploadHistoricalInvoices.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to upload the historical invoices'))
    }
  }
}

export function* onCreateOatfiPaymentMethod(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminCreateOatfiPaymentMethod.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminCreateOatfiPaymentMethod,
      {
        method: 'POST',
        params: partnerIdParams(partnerId),
      },
    )

    let business = parseOatfiBusinessResponse(payload)

    if (business === null) {
      const { payload: fetched }: { payload: unknown } = yield call(
        callSecureApi,
        Endpoints.AdminFetchOatfiBusiness,
        {
          method: 'GET',
          params: partnerIdParams(partnerId),
        },
      )

      business = parseOatfiBusinessResponse(fetched)
    }

    if (business === null) {
      throw new Error('Oatfi payment-method response is not a business')
    }

    yield put(AdminCreateOatfiPaymentMethod.success({ partnerId, business }))

    if (action.next) {
      action.next(null, business)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.oatfiBusiness.onCreateOatfiPaymentMethod] error',
    })

    yield put(AdminCreateOatfiPaymentMethod.failed(error))

    if (action.next) {
      // The four precondition failures ('Business payment method already exists', 'Partner
      // settlement settings not found', 'Partner wallet not found', 'Business not found') all
      // carry usable messages, so they surface unchanged.
      action.next(resolveErrorMessage(error, 'Failed to create the payment method'))
    }
  }
}

export function* onFetchPlaidLinkToken(action: AsyncAction<PartnerScopedPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchPlaidLinkToken.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchPlaidLinkToken,
      {
        method: 'GET',
        params: partnerIdParams(partnerId),
      },
    )

    // A response whose `hosted_link_url` is absent is a real outcome, not an error: `link.url`
    // is null and the card says the link is unavailable. Only a body that is not a link-token
    // response at all throws.
    const link = assertPlaidLink(payload)

    yield put(AdminFetchPlaidLinkToken.success({ partnerId, link }))

    if (action.next) {
      action.next(null, link)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.oatfiBusiness.onFetchPlaidLinkToken] error' })

    yield put(AdminFetchPlaidLinkToken.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to generate the bank-link URL'))
    }
  }
}

export function* oatfiBusinessSaga() {
  // Tab changes, customer changes and the underwriting card's refresh control all re-trigger
  // the reads, so only the newest run matters.
  yield takeLatest(AdminFetchOatfiBusiness.type.REQUEST, onFetchOatfiBusiness)
  yield takeLatest(AdminFetchPlaidLinkToken.type.REQUEST, onFetchPlaidLinkToken)
  // Mutations must each run. Double submits are prevented by the in-flight flags in the
  // reducer, not by dropping effects here.
  yield takeEvery(AdminSaveOatfiBusiness.type.REQUEST, onSaveOatfiBusiness)
  yield takeEvery(AdminRequestOatfiUnderwriting.type.REQUEST, onRequestOatfiUnderwriting)
  yield takeEvery(AdminUploadOatfiSupplementalData.type.REQUEST, onUploadOatfiSupplementalData)
  yield takeEvery(AdminUploadHistoricalInvoices.type.REQUEST, onUploadHistoricalInvoices)
  yield takeEvery(AdminCreateOatfiPaymentMethod.type.REQUEST, onCreateOatfiPaymentMethod)
}
